import React, { useEffect, useReducer, useState } from 'react';
import { useReducerAsync } from "use-reducer-async";
import { FormField } from '../../uiwidgets/FormField';
import { FormContainer } from '../../uiwidgets/FormContainer';
import { SubmitButton } from '../../uiwidgets/SubmitButton';
import { Row, Col, Spinner, Alert } from 'react-bootstrap';
import { getProfile } from './ProfileService';
import { ProfileFormDataReducer, profileFormDataInitialState, AsyncProfileFormDataHandlers } from './ProfileFormDataReducer';
import { ProfileFormValidationReducer, profileFormValidationInitialState } from './ProfileFormValidationReducer';
import { validateInput } from './ProfileValidationService';
import CountrySelect from 'uiwidgets/CountrySelect';
import ProvinceSelect from 'uiwidgets/ProvinceSelect';
import { kickOffFullFormValidation } from 'util/Validators';
import { CancelButton } from 'uiwidgets/CancelButton';
import PageContentContainer from 'uiwidgets/PageContentContainer';
import PostalCode from 'uiwidgets/PostalCode';

function ProfilePage(props) {

  const [formData, formDispatch] = useReducerAsync(ProfileFormDataReducer, profileFormDataInitialState, AsyncProfileFormDataHandlers);
  const [formValidityData, formValidityDispatch] = useReducer(ProfileFormValidationReducer, profileFormValidationInitialState);

  const onToastClose = () => {
    formDispatch({ type: 'CLEAR_INFO_AND_ERROR' });
  }

  useEffect(() => {
    formDispatch({ type: "FETCH_PROFILE" });
    getProfile().then(resp => {
      formDispatch({ type: "POPULATE_FORM", payload: resp.data });
    });
  }, [])

  const handleCancel = (event) => {
    getProfile().then(resp => {
      formDispatch({ type: "POPULATE_FORM", payload: resp.data });
    });
  }

  const handleCountryChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    let defaultState = "ON";
    if (value === "US"){
      defaultState = "MI";
    }
    formDispatch({ type: 'FIELD_EDIT', payload: { name: name, value: value } })
    formDispatch({ type: 'FIELD_EDIT', payload: { name: "state", value: defaultState } })
}

  const handleChange = (event) => {
    if (formData.readOnly) {
      return;
    }
    const name = event.target.name;
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    formDispatch({ type: 'FIELD_EDIT', payload: { name: name, value: value } })
  }

  const handleValidation = (event) => {
    if (formData.readOnly) {
      return;
    }
    let dispatchType = 'VALIDATE_' + event.target.name.toUpperCase();
    const name = event.target.name;
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    formValidityDispatch({ type: dispatchType, payload: { [name]: value, formData: formData } })
  }

  const handleSubmit = (event) => {
    event.preventDefault();
    let formDataEvent = {
      ...event,
      formData: formData,
      formDataIsIsolated: false
    }
    let isFormValid = kickOffFullFormValidation(formDataEvent, formValidityDispatch, validateInput);
    if (!isFormValid) {
      alert("The form still has validation errors!");
      return false;
    }

    formDispatch({
      type: 'FORM_SUBMIT', payload: { formData }
    });
  }

  return (
    <>

      {formData.status && <PageContentContainer><Alert key="info" variant='info'>
        <Spinner animation="border" role="status" variant='dark' className="me-4" />
        {formData.status}
      </Alert></PageContentContainer>}
      <FormContainer title="User Profile" submitButtonFunction={handleSubmit}
        errors={formData.errors} infoMessage={formData.infoMessage} toastCloseFunc={onToastClose} >
        <Row>
          <Col mb={4} lg={4} sm={12}>
            <FormField name="userName" label="User Name" type="text" value={formData.userName}
              placeholder="User Name" onChange={handleChange} onBlur={handleValidation}
              valstate={!formValidityData.userNameError} valMsg={formValidityData.userNameErrMsg}
              readOnly={true} />
          </Col>
          <Col mb={8} lg={8} sm={12}>
            <FormField name="email" label="Email" type="text" value={formData.email}
              placeholder="Email" onChange={handleChange} onBlur={handleValidation}
              valstate={!formValidityData.emailError} valMsg={formValidityData.emailErrMsg}
              readOnly={true} />
          </Col>
        </Row>
        <Row>
          <Col mb={6} lg={6} sm={12}>
            <FormField name="firstName" label="First Name" type="text" value={formData.firstName}
              placeholder="First Name" onChange={handleChange} onBlur={handleValidation}
              valstate={!formValidityData.firstNameError} valMsg={formValidityData.firstNameErrMsg}
              readOnly={formData.readOnly} />
          </Col>
          <Col mb={6} lg={6} sm={12}>
            <FormField name="lastName" label="Last Name" type="text" value={formData.lastName}
              placeholder="Last Name" onChange={handleChange} onBlur={handleValidation}
              valstate={!formValidityData.lastNameError} valMsg={formValidityData.lastNameErrMsg}
              readOnly={formData.readOnly} />
          </Col>
        </Row>
        <Row>
          <Col mb={7} lg={7} sm={6}>
            <FormField name="companyName" label="Company Name" type="text" value={formData.companyName}
              placeholder="Company Name" onChange={handleChange} onBlur={handleValidation}
              valstate={!formValidityData.companyNameError} valMsg={formValidityData.companyNameErrMsg}
              readOnly={formData.readOnly} />
          </Col>
        </Row>
        <Row>
          <Col mb={12} lg={12} sm={12}>
            <FormField name="address" label="Address" type="text" value={formData.address}
              placeholder="Address" onChange={handleChange} onBlur={handleValidation}
              valstate={!formValidityData.addressError} valMsg={formValidityData.addressErrMsg}
              readOnly={formData.readOnly} />
          </Col>
        </Row>
        <Row>
          <Col mb={2} lg={2} sm={6}>
            <CountrySelect name="country" onChange={handleCountryChange} onBlur={handleValidation}
              valstate={!formValidityData.countryError} valMsg={formValidityData.countryErrMsg}
              readOnly={formData.readOnly} value={formData.country} />
          </Col>
          <Col mb={4} lg={4} sm={6}>
            <ProvinceSelect name="state" onChange={handleChange} onBlur={handleValidation}
              valstate={!formValidityData.stateError} valMsg={formValidityData.stateErrMsg}
              readOnly={formData.readOnly} value={formData.state} country={formData.country}/>
          </Col>
        </Row>
        <Row>
          <Col mb={8} lg={8} sm={12}>
            <FormField name="city" label="City" type="text" value={formData.city}
              placeholder="City" onChange={handleChange} onBlur={handleValidation}
              valstate={!formValidityData.cityError} valMsg={formValidityData.cityErrMsg}
              readOnly={formData.readOnly} />
          </Col>
          <Col mb={4} lg={4} sm={6}>
            <PostalCode name="zip" label="Postal Code" type="text" value={formData.zip}
              placeholder="Postal Code" onChange={handleChange} onBlur={handleValidation}
              valstate={!formValidityData.zipError} valMsg={formValidityData.zipErrMsg}
              readOnly={formData.readOnly} country={formData.country}/>
          </Col>
        </Row>

        <Row>
          <Col mb={6} lg={6} sm={12}>
            <FormField name="mobilePhoneNumber" label="Mobile Phone Number" type="text" value={formData.mobilePhoneNumber}
              placeholder="Mobile Phone Number" onChange={handleChange} onBlur={handleValidation}
              valstate={!formValidityData.mobilePhoneNumberError} valMsg={formValidityData.mobilePhoneNumberErrMsg}
              readOnly={formData.readOnly} />
          </Col>
          <Col mb={6} lg={6} sm={12}>
            <FormField name="officePhoneNumber" label="Office Phone Number" type="text" value={formData.officePhoneNumber}
              placeholder="Office Phone Number" onChange={handleChange} onBlur={handleValidation}
              valstate={!formValidityData.officePhoneNumberError} valMsg={formValidityData.officePhoneNumberErrMsg}
              readOnly={formData.readOnly} />
          </Col>
        </Row>
        <Row><Col>
          <SubmitButton label='Save Profile' disabled={formData.readOnly} className="me-4" />
          <CancelButton label='Cancel' disabled={formData.readOnly} onClick={handleCancel} />
        </Col>
        </Row>
      </FormContainer>
    </>

  )
}

export default ProfilePage;
